import type {
  ArtLanguageCodeEnum,
  SearchSuggestionSerializer,
  IApiResponse,
  SearchResponseDataSerializer,
  SearchResponseSerializer,
  SearchResultExtra,
  SearchApiSortingEnum,
} from "@litres-libs/types";
import { rtkqApi } from "../api/rtkqApi";
import type { IArtsFacetsRequest } from "../../types/api";
import { TagsType } from "../constants";

enum SearchResponseItemTypesEnum {
  all = "all",
  books = "books",
  person = "person",
  series = "series",
  tag = "tag",
  text_book = "text_book",
  audiobook = "audiobook",
  collection = "collection",
  genre = "genre",
  podcast_episode = "podcast_episode",
  podcast = "podcast",
  publisher = "publisher",
  books_filtered_by_libraries = "books_filtered_by_libraries",
}

enum ArtTypesEnum {
  text_book = "text_book",
  audiobook = "audiobook",
  podcast = "podcast",
}

export const ALL_ENTITIES = [
  SearchResponseItemTypesEnum.person,
  SearchResponseItemTypesEnum.series,
  SearchResponseItemTypesEnum.tag,
  SearchResponseItemTypesEnum.text_book,
  SearchResponseItemTypesEnum.audiobook,
  SearchResponseItemTypesEnum.collection,
  SearchResponseItemTypesEnum.genre,
  SearchResponseItemTypesEnum.podcast_episode,
  SearchResponseItemTypesEnum.podcast,
  SearchResponseItemTypesEnum.publisher,
];

export const BOOKS_ENTITIES = [
  SearchResponseItemTypesEnum.text_book,
  SearchResponseItemTypesEnum.audiobook,
  SearchResponseItemTypesEnum.podcast,
  SearchResponseItemTypesEnum.podcast_episode,
];
export const LIBRARY_ENTITTIES = [
  SearchResponseItemTypesEnum.text_book,
  SearchResponseItemTypesEnum.audiobook,
];

interface ISearchRequest {
  q: string;
  types?: SearchResponseItemTypesEnum[];
  skip_correction?: number;
  filter_by_libraries?: boolean;
  offset?: number;
  limit?: number;
  only_from_libraries?: boolean;
  art_types?: ArtTypesEnum[];
  languages?: ArtLanguageCodeEnum[];
  only_abonement_arts?: boolean;
  only_discount_arts?: boolean;
  only_litres_subscription_arts?: boolean;
  only_selfpublished_arts?: boolean;
  only_high_rated?: boolean;
  only_litres_exclusives?: boolean;
  only_drafts?: boolean;
  only_safe_content?: boolean;
  shouldRewriteItems?: boolean;
}

export type ISearchType =
  | "text_book"
  | "audiobook"
  | "person"
  | "collection"
  | "series"
  | "genre"
  | "tag"
  | "podcast_episode"
  | "podcast"
  | "publisher"
  | "webtoon";

export const ALL_SEARCH_TYPES: ISearchType[] = [
  "text_book",
  "audiobook",
  "person",
  "collection",
  "series",
  "genre",
  "tag",
  "podcast_episode",
  "podcast",
  "publisher",
];
interface ISearchDataRequest extends IArtsFacetsRequest {
  q: string;
  types?: ISearchType[];
  skip_correction?: number;
  filter_by_libraries?: boolean;
  only_from_libraries?: boolean;
  show_unavailable?: boolean;
  o?: `${SearchApiSortingEnum}`;
}
interface ISearchExtraRequest {
  q: string;
  is_for_pda?: boolean;
  skip_correction?: number;
  show_unavailable?: boolean;
}

const getReqParamsTypes = (
  types: SearchResponseItemTypesEnum[]
): SearchResponseItemTypesEnum[] => {
  if (types.includes(SearchResponseItemTypesEnum.all)) {
    return ALL_ENTITIES;
  }

  if (types.includes(SearchResponseItemTypesEnum.books)) {
    return BOOKS_ENTITIES;
  }

  if (types.includes(SearchResponseItemTypesEnum.books_filtered_by_libraries)) {
    return LIBRARY_ENTITTIES;
  }

  if (
    (types.includes(SearchResponseItemTypesEnum.podcast) &&
      !types.includes(SearchResponseItemTypesEnum.podcast_episode)) ||
    (!types.includes(SearchResponseItemTypesEnum.podcast) &&
      types.includes(SearchResponseItemTypesEnum.podcast_episode))
  ) {
    const typeWithDuplicate = [
      ...types,
      SearchResponseItemTypesEnum.podcast,
      SearchResponseItemTypesEnum.podcast_episode,
    ];

    return typeWithDuplicate.filter(
      (item, idx) => typeWithDuplicate.indexOf(item) === idx
    );
  }

  return types;
};

export const searchApi = rtkqApi.injectEndpoints({
  endpoints: build => ({
    getSuggestions: build.query<SearchSuggestionSerializer[], { q?: string }>({
      query: ({ q }) => ({
        url: "/foundation/api/search/suggestions",
        params: {
          q,
        },
      }),
      transformResponse: (
        response: IApiResponse<SearchSuggestionSerializer[]>
      ) => {
        return response.payload.data;
      },
    }),
    getSearch: build.query<SearchResponseDataSerializer, ISearchRequest>({
      query: params => {
        const draftParams = { ...params };
        delete draftParams.shouldRewriteItems;
        return {
          url: "/foundation/api/search",
          params: {
            ...draftParams,
            types: draftParams?.types
              ? getReqParamsTypes(draftParams.types)
              : [],
          },
          headers: {
            "accept-version": "2",
          },
        };
      },
      transformResponse: (response: SearchResponseSerializer) => {
        return response.payload;
      },
    }),
    // эндпоинт возвращает extra для всех типов данных
    getSearchExtra: build.query<SearchResultExtra, ISearchExtraRequest>({
      query: params => {
        return {
          url: "/foundation/api/search",
          params: {
            ...params,
            types: ALL_SEARCH_TYPES,
          },
          headers: {
            "accept-version": "2",
          },
        };
      },
      transformResponse: (response: SearchResponseSerializer) => {
        return response.payload.extra;
      },
    }),
    // эндпоинт возвращает данные в зависимости от фильтрации и переданных типов
    getSearchData: build.query<
      SearchResponseDataSerializer,
      ISearchDataRequest
    >({
      query: params => {
        return {
          url: "/foundation/api/search",
          params,
          headers: {
            "accept-version": "2",
          },
        };
      },
      transformResponse: (response: SearchResponseSerializer) => {
        return response.payload;
      },
      providesTags: (result, _err, { types }) => {
        const bookTypes = [
          "text_book",
          "audiobook",
          "podcast",
          "podcast_episode",
          "webtoon",
        ];

        if (types?.filter(t => !bookTypes.includes(t)).length === 0) {
          return result
            ? [
                ...result.data.map(({ instance: { id } }) => ({
                  type: TagsType.ART_SHORT_LIST as const,
                  id,
                })),
                TagsType.ART_SHORT_LIST,
              ]
            : [TagsType.ART_SHORT_LIST];
        }
        return [];
      },
      async onQueryStarted(
        { q, is_for_pda, skip_correction },
        { dispatch, queryFulfilled }
      ) {
        try {
          const { data } = await queryFulfilled;
          // меняем количество книг в эндпоните getPaginatedSearchCounters в зависимости от фильтрации
          dispatch(
            searchApi.util.updateQueryData(
              "getSearchExtra",
              { q, is_for_pda, skip_correction },
              cache => {
                Object.assign(cache, data.extra);
              }
            )
          );
        } catch (e) {
          console.log(e);
        }
      },
    }),
  }),
});
